import { ArrowLeft, BookOpenText, CheckCircle2, LifeBuoy, MessageSquare, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../lib/api';
import type { AttachmentPolicy, TicketDetail, TicketSummary, TicketType } from '../lib/types';
import { NewTicketForm, ReplyBox, StatusTag, TicketRow, TicketThread, typeLabel } from '../ui/Tickets';
import { useParent } from './ParentContext';
import { ContactCard, useSupportContact } from './ContactUs';
import { HelpGuide } from './AboutHelp';

/**
 * Help (#531): one page for Contact us (#529), how to set up a child's computer and everyday tips, and your requests to
 * support (Help & Feedback, #414): ask a question, report a problem or suggest an idea, then follow the conversation. A
 * parent also sees the requests their students opened in the Student Portal. /help shows it; /support/:id opens one.
 */
/** Help (#531, #534): how to reach us, how to set up a child's computer, and everyday tips; Feedback is its own page. */
export function HelpPage() {
  const contact = useSupportContact();
  return (
    <div className="page narrow">
      <div className="page-head">
        <div>
          <h1>
            <LifeBuoy size={24} className="inline-icon" /> Help
          </h1>
          <p className="muted">How to set things up, and how to reach us.</p>
        </div>
      </div>
      {contact && <ContactCard contact={contact} />}
      <section className="card wiki-cta">
        <BookOpenText size={28} aria-hidden />
        <div className="grow">
          <h2>The ClassGuardian guide</h2>
          <p className="muted small">Every option, what it does and how it works, with screenshots. Search it or browse by topic.</p>
        </div>
        <Link to="/wiki" className="btn primary">
          Open the guide
        </Link>
      </section>
      <HelpGuide />
      <section className="card stack">
        <h2>Have a question, problem or idea?</h2>
        <p className="muted small">Send it to ClassGuardian support and follow the replies in Feedback.</p>
        <div>
          <Link to="/feedback" className="btn">
            <MessageSquare size={16} /> Send feedback
          </Link>
        </div>
      </section>
    </div>
  );
}

/**
 * Feedback (#534): your requests to ClassGuardian support (questions, problems, ideas) and the replies. /feedback lists
 * them; /support/:id opens one (the address reply emails link to).
 */
export function FeedbackPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useParent();
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [writing, setWriting] = useState(false);
  const [allowed, setAllowed] = useState<TicketType[] | undefined>(undefined);
  const attachments = useAttachmentPolicy();
  useEffect(() => {
    api.get<{ types: TicketType[] }>('/api/support/options').then((o) => setAllowed(o.types)).catch(() => undefined);
  }, []);

  const load = useCallback(() => {
    api.get<TicketSummary[]>('/api/support/tickets').then(setTickets).catch(() => setTickets([]));
  }, []);
  useEffect(load, [load]);

  if (id) return <TicketPage id={id} onBack={() => (navigate('/feedback'), load())} />;

  const active = tickets?.filter((t) => t.status !== 'resolved' && t.status !== 'closed') ?? [];
  const done = tickets?.filter((t) => t.status === 'resolved' || t.status === 'closed') ?? [];

  return (
    <div className="page narrow">
      <div className="page-head">
        <div>
          <h1>
            <MessageSquare size={24} className="inline-icon" /> Feedback
          </h1>
          <p className="muted">Ask a question, tell us what’s not working, or share an idea. We’ll reply here and by email.</p>
        </div>
      </div>

      {writing ? (
        <section className="card stack">
          <h2>New request</h2>
          <NewTicketForm
            allowed={allowed}
            attachments={attachments}
            onSubmit={async (t) => {
              try {
                const made = await api.post<TicketDetail>('/api/support/tickets', t);
                toast({ text: `Sent. Your request number is #${made.number}.`, tone: 'good' });
                setWriting(false);
                navigate(`/support/${made.id}`);
              } catch (e) {
                toast({ text: (e as Error).message, tone: 'alert' });
              }
            }}
          />
          <div>
            <button className="btn" onClick={() => setWriting(false)}>
              Cancel
            </button>
          </div>
        </section>
      ) : (
        <div>
          <button className="btn primary" onClick={() => setWriting(true)}>
            <Plus size={16} /> New request
          </button>
        </div>
      )}

      <section className="card stack">
        <h2>Your requests</h2>
        {!tickets ? (
          <p className="muted">Loading…</p>
        ) : !tickets.length ? (
          <p className="muted">Nothing yet. Questions, problems and ideas you send appear here.</p>
        ) : (
          <>
            {active.map((t) => (
              <TicketRow key={t.id} t={t} onOpen={() => navigate(`/support/${t.id}`)} />
            ))}
            {!!done.length && <h3 className="muted small">Resolved and closed</h3>}
            {done.map((t) => (
              <TicketRow key={t.id} t={t} onOpen={() => navigate(`/support/${t.id}`)} />
            ))}
          </>
        )}
      </section>
    </div>
  );
}

/** What the server allows for screenshots on requests (#446). */
function useAttachmentPolicy() {
  const [policy, setPolicy] = useState<AttachmentPolicy | null>(null);
  useEffect(() => {
    api
      .get<{ attachments?: AttachmentPolicy }>('/api/support/options')
      .then((o) => setPolicy(o.attachments ?? null))
      .catch(() => undefined);
  }, []);
  return policy;
}

function TicketPage({ id, onBack }: { id: string; onBack: () => void }) {
  const { toast } = useParent();
  const attachments = useAttachmentPolicy();
  const [t, setT] = useState<TicketDetail | null | undefined>(undefined);
  useEffect(() => {
    api.get<TicketDetail>(`/api/support/tickets/${id}`).then(setT).catch(() => setT(null));
  }, [id]);
  const act = (fn: () => Promise<TicketDetail>) =>
    fn()
      .then((next) => (setT(next), true))
      .catch((e) => (toast({ text: (e as Error).message, tone: 'alert' }), false));

  return (
    <div className="page narrow">
      <div>
        <button className="btn small" onClick={onBack}>
          <ArrowLeft size={15} /> All requests
        </button>
      </div>
      {t === undefined ? (
        <p className="muted">Loading…</p>
      ) : t === null ? (
        <p className="muted">That request isn’t here anymore.</p>
      ) : (
        <section className="card stack">
          <div className="row-between">
            <span className="muted small">
              #{t.number} · {typeLabel(t.type)}
              {t.openedBy.kind === 'student' && ` · from ${t.openedBy.name} (student)`}
            </span>
            <StatusTag status={t.status} />
          </div>
          <h2>{t.subject}</h2>
          <TicketThread messages={t.messages} issue={{ number: t.issueNumber }} />
          {t.status === 'closed' ? (
            <p className="muted small">This request is closed. Start a new one if you still need help.</p>
          ) : (
            <>
              <ReplyBox
                attachments={attachments}
                onSend={(text, files) => act(() => api.post<TicketDetail>(`/api/support/tickets/${t.id}/messages`, { body: text, attachments: files }))}
                placeholder={t.status === 'resolved' ? 'Still need help? Reply to reopen it.' : 'Write a reply…'}
              />
              {t.status !== 'resolved' && (
                <div>
                  <button className="btn small" onClick={() => act(() => api.post<TicketDetail>(`/api/support/tickets/${t.id}/resolve`))}>
                    <CheckCircle2 size={15} /> Mark resolved
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
